import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import logo from "../assets/Logo.png";

const LINKS = [
  { name: "Nosotros", path: "/nosotros" },
  { name: "Productos", path: "/productos" },
  { name: "Politicas", path: "/politicas" },
  { name: "Ubicación", path: "/ubicacion" },
  { name: "Contacto", path: "/contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const location = useLocation();

  /* Close mobile menu on route change */
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  /* Navbar background on scroll */
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);

    window.addEventListener("scroll", handler, { passive: true });

    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: scrolled ? "1rem 4rem" : "1.5rem 4rem",
    background: scrolled ? "rgba(26,24,20,0.95)" : "transparent",
    backdropFilter: scrolled ? "blur(20px)" : "none",
    borderBottom: scrolled
      ? "1px solid rgba(201,151,58,0.2)"
      : "none",
    transition: "all 0.5s ease",
  };

  /* Shared link style */
  const linkBase = {
    fontFamily: "'Jost', sans-serif",
    fontSize: "0.78rem",
    fontWeight: 400,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    textDecoration: "none",
    position: "relative",
    transition: "color 0.3s",
  };

  return (
    <>
      {/* ── MAIN NAV BAR ── */}
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={navStyle}
      >
        {/* Logo */}
        <NavLink
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            textDecoration: "none",
          }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{
              type: "spring",
              stiffness: 400,
            }}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={logo}
              alt="Monaco Aberturas"
              style={{
                width: "58px",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </motion.div>

          <div style={{ display: "none" }} className="sm-show">
            <p
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "#f0c96b",
                fontSize: "1.3rem",
                fontWeight: 600,
                letterSpacing: "0.12em",
                lineHeight: 1.1,
                margin: 0,
              }}
            >
              Monaco Aberturas
            </p>

            <p
              style={{
                fontFamily: "'Jost', sans-serif",
                color: "#c9973a",
                fontSize: "0.62rem",
                fontWeight: 300,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              Soluciones de Vanguardia
            </p>
          </div>
        </NavLink>

        {/* Desktop links */}
        <ul
          style={{
            display: "flex",
            gap: "2.5rem",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
          className="desktop-nav"
        >
          {LINKS.map((link) => (
            <li key={link.path}>
              <NavLinkItem
                link={link}
                linkBase={linkBase}
              />
            </li>
          ))}
        </ul>

        {/* CTA + hamburger */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <motion.a
            href="https://wa.me/542236901020"
            target="_blank"
            rel="noreferrer"
            whileHover={{
              background: "#c9973a",
              color: "#1a1814",
            }}
            style={{
              border: "1px solid #c9973a",
              color: "#f0c96b",
              padding: "0.6rem 1.6rem",
              fontFamily: "'Jost', sans-serif",
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              textDecoration: "none",
              transition: "background 0.3s, color 0.3s",
            }}
            className="nav-cta-btn"
          >
            Consultá ahora
          </motion.a>

          {/* Hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="hamburger-btn"
            style={{
              background: "none",
              border: "none",
              color: "#f0c96b",
              cursor: "pointer",
              padding: "4px",
              display: "none",
            }}
            aria-label="Menú"
          >
            <AnimatePresence mode="wait">
              {mobileOpen ? (
                <motion.svg
                  key="close"
                  initial={{
                    rotate: -90,
                    opacity: 0,
                  }}
                  animate={{
                    rotate: 0,
                    opacity: 1,
                  }}
                  exit={{
                    rotate: 90,
                    opacity: 0,
                  }}
                  transition={{ duration: 0.2 }}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                  />
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                  />
                </motion.svg>
              ) : (
                <motion.svg
                  key="open"
                  initial={{
                    rotate: 90,
                    opacity: 0,
                  }}
                  animate={{
                    rotate: 0,
                    opacity: 1,
                  }}
                  exit={{
                    rotate: -90,
                    opacity: 0,
                  }}
                  transition={{ duration: 0.2 }}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <line
                    x1="3"
                    y1="6"
                    x2="21"
                    y2="6"
                  />
                  <line
                    x1="3"
                    y1="12"
                    x2="21"
                    y2="12"
                  />
                  <line
                    x1="3"
                    y1="18"
                    x2="21"
                    y2="18"
                  />
                </motion.svg>
              )}
            </AnimatePresence>
          </button>
        </div>
      </motion.nav>

      {/* ── MOBILE MENU ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: 1,
              height: "auto",
            }}
            exit={{
              opacity: 0,
              height: 0,
            }}
            transition={{
              duration: 0.4,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              position: "fixed",
              top: 72,
              left: 0,
              right: 0,
              zIndex: 999,
              background: "rgba(15,13,11,0.97)",
              backdropFilter: "blur(24px)",
              borderBottom:
                "1px solid rgba(201,151,58,0.15)",
              overflow: "hidden",
            }}
            className="mobile-menu"
          >
            <ul
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "2.5rem 0",
                gap: "1.75rem",
                listStyle: "none",
                margin: 0,
              }}
            >
              {LINKS.map((link, i) => (
                <motion.li
                  key={link.path}
                  initial={{
                    opacity: 0,
                    y: 12,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    y: 8,
                  }}
                  transition={{
                    delay: i * 0.06,
                  }}
                >
                  <NavLink
                    to={link.path}
                    onClick={() =>
                      setMobileOpen(false)
                    }
                    style={({ isActive }) => ({
                      ...linkBase,
                      color: isActive
                        ? "#f0c96b"
                        : "rgba(249,245,239,0.8)",
                    })}
                  >
                    {link.name}
                  </NavLink>
                </motion.li>
              ))}

              <motion.li
                initial={{
                  opacity: 0,
                  y: 12,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay:
                    LINKS.length * 0.06 + 0.05,
                }}
              >
                <a
                  href="https://wa.me/542236901020"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    border: "1px solid #c9973a",
                    color: "#f0c96b",
                    padding: "0.75rem 2.5rem",
                    textDecoration: "none",
                    fontFamily: "'Jost', sans-serif",
                    fontSize: "0.75rem",
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                  }}
                >
                  WhatsApp
                </a>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 1024px) {
          .desktop-nav {
            display: none !important;
          }
        }

        @media (max-width: 1024px) {
          .hamburger-btn {
            display: block !important;
          }
        }

        @media (max-width: 640px) {
          .nav-cta-btn {
            display: none !important;
          }
        }

        @media (min-width: 640px) {
          .sm-show {
            display: block !important;
          }
        }

        nav {
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
        }

        @media (min-width: 1024px) {
          nav {
            padding-left: 4rem !important;
            padding-right: 4rem !important;
          }
        }
      `}</style>
    </>
  );
}

/* ── Individual nav link ── */
function NavLinkItem({ link, linkBase }) {
  return (
    <NavLink
      to={link.path}
      style={({ isActive }) => ({
        ...linkBase,
        color: isActive ? "#f0c96b" : "#f9f5ef",
      })}
    >
      {({ isActive }) => (
        <span
          style={{
            position: "relative",
            display: "inline-block",
          }}
        >
          {link.name}

          {/* Active underline */}
          <motion.span
            style={{
              position: "absolute",
              bottom: -4,
              left: 0,
              right: 0,
              height: 1,
              background: "#c9973a",
              display: "block",
            }}
            initial={false}
            animate={{
              scaleX: isActive ? 1 : 0,
              opacity: isActive ? 1 : 0,
            }}
            transition={{ duration: 0.3 }}
          />

          {/* Hover underline */}
          <span
            style={{
              position: "absolute",
              bottom: -4,
              left: 0,
              right: 0,
              height: 1,
              background: "rgba(201,151,58,0.4)",
              display: "block",
              transform: "scaleX(0)",
              transformOrigin: "right",
              transition: "transform 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform =
                "scaleX(1)";
              e.currentTarget.style.transformOrigin =
                "left";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform =
                "scaleX(0)";
              e.currentTarget.style.transformOrigin =
                "right";
            }}
          />
        </span>
      )}
    </NavLink>
  );
}
